// src/api/sentNotificationsApi.ts
import { ENV } from '../../config/runtimeEnv';
import type { SentNotification, SentNotificationDetail, SentNotificationFilters, SentNotificationStats } from '../../types/communications/NotificationTypes';
import type { UserUuid } from '../../types/identity';
import { createServiceClient } from '../../lib/http/client';

export const API_BASE_URL = ENV.VITE_NOTIFICATION_BASE_URL;

// Create axios instance with default config
const createApiInstance = () => {
	return createServiceClient({
		baseURL: API_BASE_URL,
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

export const fetchSentNotifications = async (filters: SentNotificationFilters = {}): Promise<SentNotification[]> => {
	try {
		const api = createApiInstance();
		const response = await api.get<SentNotification[] | { data: SentNotification[] }>('/notifications/sent', {
			params: filters,
		});
		// Handle both plain array and Laravel paginated response { data: [...] }
		const result = Array.isArray(response.data) ? response.data : (response.data as { data: SentNotification[] }).data ?? [];
		return result;
	} catch (error) {
		console.error('Error fetching sent notifications:', error);
		throw error;
	}
};

/**
 * ONE sent notification, with its rendered body.
 */
export const fetchSentNotification = async (id: number): Promise<SentNotificationDetail> => {
	const api = createApiInstance();
	const response = await api.get<SentNotificationDetail>(`/notifications/sent/${id}`);
	return response.data;
};

// Get sent notifications for a specific recipient email
export const fetchSentNotificationsByEmail = async (recipientEmail: string): Promise<SentNotification[]> => {
	return fetchSentNotifications({ recipient_email: recipientEmail });
};

// Get sent notifications for a specific recipient user ID
export const fetchSentNotificationsByUserId = async (recipientUserId: UserUuid): Promise<SentNotification[]> => {
	return fetchSentNotifications({ recipient_user_id: recipientUserId });
};

// Get sent notifications for a specific recipient phone
export const fetchSentNotificationsByPhone = async (recipientPhone: string): Promise<SentNotification[]> => {
	return fetchSentNotifications({ recipient_phone: recipientPhone });
};

// Get sent notifications for a specific template
export const fetchSentNotificationsByTemplate = async (templateName: string): Promise<SentNotification[]> => {
	return fetchSentNotifications({ template_name: templateName });
};

// Get sent notifications within a date range
export const fetchSentNotificationsByDateRange = async (
	dateFrom: string,
	dateTo: string
): Promise<SentNotification[]> => {
	return fetchSentNotifications({ date_from: dateFrom, date_to: dateTo });
};

/**
 * Notification statistics, derived client-side from a FILTERED list call.
 *
 * ⚠️ THE FILTER IS MANDATORY. This used to call `fetchSentNotifications()` with no arguments,
 * which is the exact unfiltered request SEC (ClickUp 86d3uy3fg) closed — it asked the service
 * for "everything" and, before the fix, got 50 rows of other patients' clinical bodies back.
 * There is still no stats endpoint; until there is, a caller must say whose statistics it wants.
 * An empty filter is refused here rather than widened into a clinic-wide read.
 */
export const fetchNotificationStats = async (
	filters: SentNotificationFilters,
	period: 'day' | 'week' | 'month' = 'week'
): Promise<SentNotificationStats | null> => {
	if (!filters || Object.values(filters).every((value) => value === undefined || value === null || value === '')) {
		console.error('fetchNotificationStats requires an explicit filter (e.g. recipient_user_id)');
		return null;
	}

	try {
		// This is a placeholder for a stats endpoint that could be implemented
		const notifications = await fetchSentNotifications(filters);

		// Calculate basic stats from the data we have
		const emailNotifications = notifications.filter(n => n.recipient_email);
		const smsNotifications = notifications.filter(n => n.recipient_phone);

		// Get recent activity (last 10 notifications)
		const recentActivity = notifications
			.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
			.slice(0, 10);

		return {
			total_sent: notifications.length,
			email_sent: emailNotifications.length,
			sms_sent: smsNotifications.length,
			recent_activity: recentActivity
		};
	} catch (error) {
		console.error('Error fetching notification stats:', error);
		return null;
	}
};

// Group sent notifications by template
export const groupNotificationsByTemplate = (notifications: SentNotification[]): Record<string, SentNotification[]> => {
	return notifications.reduce((groups, notification) => {
		const template = notification.template_name;
		if (!groups[template]) {
			groups[template] = [];
		}
		groups[template].push(notification);
		return groups;
	}, {} as Record<string, SentNotification[]>);
};

// Group sent notifications by date
export const groupNotificationsByDate = (notifications: SentNotification[]): Record<string, SentNotification[]> => {
	return notifications.reduce((groups, notification) => {
		const date = new Date(notification.created_at).toISOString().split('T')[0];
		if (!groups[date]) {
			groups[date] = [];
		}
		groups[date].push(notification);
		return groups;
	}, {} as Record<string, SentNotification[]>);
};

// Format notification content for display (strip HTML, limit length)
export const formatNotificationContent = (content: string, maxLength: number = 100): string => {
	// Strip HTML tags
	const stripped = content.replace(/<[^>]*>/g, '');

	// Limit length
	if (stripped.length <= maxLength) {
		return stripped;
	}

	return stripped.substring(0, maxLength) + '...';
};

// Get notification type from content
export const getNotificationType = (notification: SentNotification): 'email' | 'sms' | 'push' | 'both' => {
	// Prefer the explicit channel field when available
	if (notification.channel) {
		return notification.channel;
	}
	if (notification.recipient_email && notification.recipient_phone) {
		return 'both';
	} else if (notification.recipient_email) {
		return 'email';
	} else if (notification.recipient_phone) {
		return 'sms';
	}
	return 'email'; // default
};
